// DUAL-ROLE-1-UI — the ONE authorization change this goal permits, and nothing wider.
import { mayReachTeacherRoutes } from "@/lib/workspaceSwitch";
import {
  ROLE,
  canAccessTenantRole,
  getDefaultHomeRoute,
  getUserTenantRole,
  isAdminUser,
  isSchoolAdminUser,
  isSuperAdminUser,
  isTeacherUser,
  isTrainingAdminUser,
  normalizePath,
} from "@/lib/userRoutes";

// ADMIN-A1 — the DIRECT SWAP: school_admin lives in the new /admin* shell. The five places + the
// shared utilities a school_admin still needs (video playback, notifications, onboarding). The legacy
// admin landings (/dashboard, /teachers, /coaching, /reports, /observation, /recognition-review,
// /school-setup, /settings) are DROPPED here on purpose — a school_admin who hits one is redirected
// home (ProtectedRoute → getHomeRoute → /admin), which is the "legacy redirects into the shell" ruling.
// (training_admin keeps the legacy surfaces via TRAINING_ROUTES; master_admin is untouched.)
const ADMIN_ROUTES = [
  "/admin",
  "/videos",
  "/record",
  "/settings/notifications",
  "/notifications",
  "/onboarding",
];

const TRAINING_ROUTES = [
  "/dashboard",
  "/teachers",
  "/cohorts",
  "/coaching",
  "/master-schedule",
  "/observation",
  "/record",
  "/reports",
  "/videos",
  "/all-star-library",
  "/school-setup",
  "/admin/settings",
  "/settings/notifications",
  "/notifications",
  "/onboarding",
];

const TEACHER_ROUTES = [
  "/my-workspace",
  "/my-profile",
  "/my-lessons",
  // ADDENDUM 2 item 2 — the teacher shell renders a nav item to /my-growth (:283 desktop, :289
  // mobile) and this list did not contain it. Harmless while the route's own allowedTenantRoles is
  // the only gate, but DUAL-ROLE-1-UI's safeguard reads THIS list, so a dual-role admin in teacher
  // mode was offered a nav item the guard then refused. Audited the rest of getRoleShellConfig
  // against all four allow-lists: /my-growth was the only nav target missing from every one of them.
  "/my-growth",
  // ADDENDUM 3 — the same omission as /my-growth above, on a wider surface. /my-plans is declared
  // allowedTenantRoles={["teacher"]} (App.js:573) and is reached from FOUR teacher surfaces: the
  // shell's own nav item (LayoutShell.js:547 `nav.myPlanning`), TeacherWorkspacePage:1380,
  // TeacherSelfProfilePage:410, and TeacherLessonView:932, which deep-links a specific plan. As with
  // /my-growth this is harmless while the route's own allowedTenantRoles is the only gate, but
  // DUAL-ROLE-1-UI's safeguard reads THIS list, so a dual-role admin in teacher mode was offered the
  // document repository from four places and refused at each.
  "/my-plans",
  "/my-coaching",
  "/my-badges",
  "/teacher",
  "/lessons",
  "/coaching",
  "/teachers-room",
  "/videos",
  "/record",
  "/all-star-library",
  "/settings/notifications",
  "/notifications",
  "/consent",
  "/privacy",
];

const SUPER_ADMIN_ROUTES = [
  "/master-admin",
  "/videos",
  "/onboarding",
  "/teachers",
  "/settings/notifications",
  "/notifications",
];

const PUBLIC_ROUTES = [
  "/login",
  "/request-access",
  "/forgot-password",
  "/reset-password",
  "/privacy",
];

const startsWithAny = (path, prefixes) =>
  prefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));

export const getUserRole = (user) => getUserTenantRole(user);

export const isSuperAdmin = (user) => isSuperAdminUser(user);

export const isAdmin = (user) => isAdminUser(user);

export const isTeacher = (user) => isTeacherUser(user);

export const getHomeRoute = (user) => getDefaultHomeRoute(user);

export const getDefaultRouteForUser = getDefaultHomeRoute;

export const getRoleHomePath = getDefaultHomeRoute;

export const getRoleShell = (user) => {
  const role = getUserTenantRole(user);

  if (role === ROLE.SUPER_ADMIN) return "master";
  if (role === ROLE.TRAINING_ADMIN) return "training";
  if (role === ROLE.SCHOOL_ADMIN) return "admin";
  if (role === ROLE.TEACHER) return "teacher";
  return "teacher";
};

export const getEffectiveWorkspaceMode = (user) => {
  const role = getUserTenantRole(user);

  if (role === ROLE.SUPER_ADMIN) return "master";
  if (role === ROLE.TRAINING_ADMIN) return "training";
  if (role === ROLE.SCHOOL_ADMIN) return "school";
  return "teacher";
};

export const canAccess = (user, routePath = "", allowedTenantRoles = []) => {
  const path = normalizePath(routePath).toLowerCase();

  if (path === "/") {
    return Boolean(user);
  }

  if (startsWithAny(path, PUBLIC_ROUTES)) {
    return true;
  }

  if (!user) {
    return false;
  }

  // DUAL-ROLE-1-UI — THE SAFEGUARD, and the whole of it: an account the SERVER says is eligible for
  // the teacher workspace may reach teacher routes. Read from /api/me's workspace_eligibility, never
  // from active_workspace — a user-settable value must not widen what a user may reach — and never
  // from a role string. It opens teacher routes to a dual-role user and nothing else: the admin,
  // training and super-admin allow-lists below are untouched, and every request is still authorized
  // by the server, per request, from the token.
  if (
    mayReachTeacherRoutes(user) &&
    !isTeacherUser(user) &&
    startsWithAny(path, TEACHER_ROUTES)
  ) {
    return true;
  }

  if (Array.isArray(allowedTenantRoles) && allowedTenantRoles.length > 0) {
    if (
      isSuperAdminUser(user) &&
      !allowedTenantRoles.includes(ROLE.SUPER_ADMIN) &&
      !user?.is_preview_mode
    ) {
      return startsWithAny(path, SUPER_ADMIN_ROUTES);
    }
    return canAccessTenantRole(user, allowedTenantRoles);
  }

  if (isSuperAdminUser(user)) {
    return startsWithAny(path, SUPER_ADMIN_ROUTES);
  }

  if (isSchoolAdminUser(user)) {
    return startsWithAny(path, ADMIN_ROUTES);
  }

  if (isTrainingAdminUser(user)) {
    return startsWithAny(path, TRAINING_ROUTES);
  }

  if (isTeacherUser(user)) {
    return startsWithAny(path, TEACHER_ROUTES);
  }

  return false;
};

export const canAccessRoute = canAccess;

export const getRoleShellConfig = (user) => {
  const role = getUserTenantRole(user);

  // B6-f: getRoleShellConfig is the SINGLE SOURCE OF TRUTH for nav. These arrays
  // were MIGRATED VERBATIM from LayoutShell's former hardcoded ROLE_NAV_ITEMS /
  // ROLE_MOBILE_NAV_ITEMS (the stale 3-item stubs that used to live here were
  // wrong and have been replaced wholesale). Items are icon-free ({ to, label,
  // end }); LayoutShell maps route -> lucide icon when rendering, so this lib
  // stays React/lucide-free. Desktop and mobile sets differ per role and are both
  // exposed (navItems + mobileNavItems) so admin/master mobile rendering is preserved.
  if (role === ROLE.SUPER_ADMIN) {
    return {
      role,
      navKey: getRoleShell(user),
      homeRoute: "/master-admin",
      dashboardRoute: "/master-admin",
      label: "Super admin",
      navItems: [
        { label: "Overview", to: "/master-admin", end: true },
        { label: "Organizations", to: "/master-admin/organizations" },
        { label: "Users", to: "/master-admin/users" },
        { label: "Workspaces", to: "/master-admin/workspaces" },
        { label: "Videos", to: "/master-admin/videos" },
        { label: "Dependencies", to: "/master-admin/dependencies" },
        { label: "Audit", to: "/master-admin/audit" },
        { label: "Incidents", to: "/master-admin/incidents" },
        { label: "Support", to: "/master-admin/support" },
      ],
      mobileNavItems: [
        { label: "Overview", to: "/master-admin", end: true },
        { label: "Users", to: "/master-admin/users" },
        { label: "Orgs", to: "/master-admin/organizations" },
        { label: "Health", to: "/master-admin/dependencies" },
        { label: "Audit", to: "/master-admin/audit" },
      ],
    };
  }

  if (role === ROLE.TRAINING_ADMIN) {
    return {
      role,
      navKey: getRoleShell(user),
      homeRoute: "/dashboard",
      dashboardRoute: "/dashboard",
      label: "Training Admin",
      navItems: [
        { label: "nav.dashboard", to: "/dashboard", end: true },
        // ADMIN-COHORTS Part 2 — a PERMANENT door back to /onboarding for training_admin. She gets
        // LayoutShell (not AdminShell), so she never had the rail Getting Started nor /admin/setup, and
        // the SetupBanner self-hides once required steps complete — leaving no visible door. This pill is
        // unconditional (not the school-admin useGettingStartedNavItem hook), reuses the existing
        // adminNav.gettingStarted string (no invented Hebrew), and the /onboarding route already admits
        // training_admin. Desktop nav only, alongside Cohorts — mirroring how Cohorts itself is desktop-only.
        { label: "adminNav.gettingStarted", to: "/onboarding" },
        { label: "nav.cohorts", to: "/cohorts" },
        { label: "nav.trainees", to: "/teachers" },
        { label: "nav.observation", to: "/observation/new" },
        { label: "nav.coaching", to: "/coaching" },
        { label: "nav.reports", to: "/reports" },
        { label: "nav.settings", to: "/school-setup" },
      ],
      mobileNavItems: [
        { label: "nav.dashboard", to: "/dashboard", end: true },
        { label: "nav.trainees", to: "/teachers" },
        { label: "nav.observeShort", to: "/observation/new" },
        { label: "nav.coaching", to: "/coaching" },
        { label: "nav.reports", to: "/reports" },
        { label: "nav.settings", to: "/school-setup" },
      ],
    };
  }

  if (role === ROLE.SCHOOL_ADMIN) {
    // ADMIN-A1 — the principal's five places (order ruled). Home · School Progress · My Team ·
    // Lesson Library · School Setup. Labels are i18n keys (adminNav.*), resolved at the rail.
    //
    // ONBOARD-1 B2 adds Access as a SIXTH, placed LAST so the five ruled places keep their ruled
    // order and their positions. Access is occasional (approve a new teacher, remove a leaver), so
    // last is also where it belongs by frequency — but it is a sixth place in a five-place rule, and
    // that is a ruling to confirm, not mine to make silently.
    const adminNav = [
      { label: "adminNav.home", to: "/admin", end: true },
      // ADMIN-D2·S2 (R3c) — SUPERSESSION, not a seventh place: Growth takes School Progress's
      // ruled position. A4's page leaves the rail; its naked 1-4 values render nowhere. The
      // ADMIN-A1 five-places doctrine is preserved (Access remains the declared sixth).
      { label: "adminNav.growth", to: "/admin/growth" },
      // W39 (C-359 §2, C-362 §5) — "My leadership": the admin's OWN goals, beside the goals they
      // set for others. ⚑ THIS IS A NEW RAIL PLACE AND THE POSITION IS A RULING, NOT A PREFERENCE.
      // The note above and the one on /admin/schedule below both say a new place is "a ruling to
      // confirm, not mine to make silently", so it is said out loud: C-359 §2 and design §1.1 order
      // the item; the POSITION — immediately after Growth, inside the `school` group — is LANE-3's
      // choice, declared on the PR and cheap to move. It is in NAV_GROUPS' school group so it does
      // not land in AdminShell's `ungrouped` safety net.
      //
      // ⚑ SCHOOL ADMINS ONLY. This list is the school_admin shell's; training_admin is out of scope
      // (C-362 §5) and has its own nav below, and a super admin gets the master-admin shell and is
      // redirected away from the route besides. GROWTH-ENGINE's IN-271 had said school_admin +
      // training_admin; C-362 §5 is later and narrower, and it governs.
      { label: "adminNav.myLeadership", to: "/admin/my-leadership" },
      { label: "adminNav.team", to: "/admin/team" },
      // ⚑ F-63 / F-25 — Reports. C-374 §3 and C-375 rule the admin nav at FIVE entries and name
      // דוחות (Reports) as one of them, so this is a RULED place rather than another appended
      // one. The rail is still seven here because F-73 is the item that cuts it to five and
      // rehomes the rest; this entry is placed in its ruled position now so that F-73 is a
      // deletion of four rather than a rearrangement of eight.
      // ⚑ `adminNav.reports`, NOT `reports.nav`. I wired `reports.nav` before LANE-1 shipped
      // the nav pair, and IN-289 rules the five nav labels as `adminNav.*` -- `adminNav.reports`
      // now exists on main with the same two values. Two keys holding one label is the exact trap
      // LANE-1 flagged in that note (`adminNav.progress` had no call site and Hebrew identical to
      // growth's, so picking the wrong one was invisible until someone read Hebrew). The rail uses
      // the nav namespace like its four siblings; `reports.nav` stays for the reports page itself.
      { label: "adminNav.reports", to: "/admin/reports" },
      { label: "adminNav.library", to: "/admin/library" },
      { label: "adminNav.setup", to: "/admin/setup" },
      // F14 SETUP-LOOP-1 / C-116 item 6 (option i) — RULED under the six-places doctrine, which
      // roleRouter's own note above calls "a ruling to confirm, not mine to make silently". It is
      // confirmed, so here it is.
      //
      // Placed AFTER Setup and BEFORE Access on purpose: the five ruled places keep their ruled
      // order AND their positions, and Access stays appended last — the arrangement AdminShell's
      // own test defends, whose comment asks the next place to come here and say so. This does.
      { label: "frameworksPage.recordingCompliancePolicy", to: "/admin/schedule" },
      { label: "adminNav.access", to: "/admin/access" },
    ];
    return {
      role,
      navKey: getRoleShell(user),
      homeRoute: "/admin",
      dashboardRoute: "/admin",
      label: "School Admin",
      navItems: adminNav,
      mobileNavItems: adminNav,
    };
  }

  if (role === ROLE.TEACHER) {
    return {
      role,
      navKey: getRoleShell(user),
      homeRoute: "/my-workspace",
      dashboardRoute: "/my-workspace",
      label: "Teacher",
      // T3 four-place shell: Home · My Lessons · My Growth · Recognition. Home/Lessons keep
      // their existing paths (/my-workspace, /my-lessons) — relabel only, no path churn. Profile
      // moves off the primary bar into the avatar menu; Growth + Recognition are new places.
      // HEB-2: labels are i18n KEYS (resolved via t(item.label) at the LayoutShell render sites) so
      // this lib stays React/i18n-free. Super-admin nav stays English literals (HEB-3, doctrine-excluded).
      navItems: [
        { label: "nav.homeShort", to: "/my-workspace", end: true },
        { label: "nav.myLessons", to: "/my-lessons" },
        { label: "nav.myGrowth", to: "/my-growth" },
        { label: "nav.recognition", to: "/teachers-room" },
      ],
      mobileNavItems: [
        { label: "nav.homeShort", to: "/my-workspace", end: true },
        { label: "nav.myLessons", to: "/my-lessons" },
        { label: "nav.myGrowth", to: "/my-growth" },
        { label: "nav.recognition", to: "/teachers-room" },
      ],
    };
  }

  return {
    role: ROLE.GUEST,
    navKey: "guest",
    homeRoute: "/login",
    dashboardRoute: "/login",
    label: "Guest",
    navItems: [],
    mobileNavItems: [],
  };
};

const roleRouter = {
  getUserRole,
  isSuperAdmin,
  isAdmin,
  isTeacher,
  getHomeRoute,
  getDefaultRouteForUser,
  getRoleHomePath,
  getRoleShell,
  getRoleShellConfig,
  getEffectiveWorkspaceMode,
  canAccess,
  canAccessRoute,
};

export default roleRouter;
